import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------------------------------------------
 * Auth token storage
 * ------------------------------------------------------------------
 * We primarily use HTTP-Only cookies for authentication. However in
 * cross-origin production deployments (frontend on custom domain,
 * backend on emergentagent.com) browsers silently drop third-party
 * cookies (Safari ITP, Chrome 3PCD, Firefox ETP). As a transparent
 * fallback we also persist access + refresh tokens in localStorage
 * and attach them as `Authorization: Bearer ...` on every request.
 * If cookies work, the Authorization header is simply ignored by the
 * server (the server prefers cookies). If cookies are blocked, the
 * Bearer header keeps the user authenticated.
 * ------------------------------------------------------------------*/
const ACCESS_KEY = 'zv_at';
const REFRESH_KEY = 'zv_rt';

export function storeTokens(accessToken, refreshToken) {
  try {
    if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  } catch { /* localStorage unavailable (private mode) — cookies still work */ }
}

export function clearTokens() {
  try { localStorage.removeItem(ACCESS_KEY); localStorage.removeItem(REFRESH_KEY); }
  catch { /* ignore */ }
}

function getAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY); } catch { return null; }
}

function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 45000, // 45 s — accommodates slow LLM endpoints like /aegis/simulate
});

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  // Caller can suppress Bearer attachment by explicitly passing
  // `Authorization: ''` (used by the login/register flows so a stale
  // token from a previous account never leaks into an auth request).
  const authHeaderExplicit = Object.prototype.hasOwnProperty.call(config.headers, 'Authorization');
  if (!authHeaderExplicit) {
    const tok = getAccessToken();
    if (tok) config.headers.Authorization = `Bearer ${tok}`;
  } else if (config.headers.Authorization === '') {
    delete config.headers.Authorization;
  }
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers.Pragma = 'no-cache';
  }
  return config;
});

/* ------------------------------------------------------------------
 * In-memory GET cache (per tab)
 * ------------------------------------------------------------------
 * Many components hit the same endpoint repeatedly as the user navigates
 * (e.g. /plan/me, /billing/plans, /content/public, /admin/seo).
 * A short TTL cache de-duplicates in-flight calls and serves warm hits
 * instantly — dramatically reducing perceived latency and server load.
 *
 * Usage:
 *   api.getCached('/plan/me', { ttl: 60_000 })
 *   api.invalidateCache('/plan/me')  // on mutation
 * ------------------------------------------------------------------*/
const _cache = new Map();  // key -> { expires, data }
const _inflight = new Map(); // key -> Promise

function cacheKey(url, params) {
  try { return url + (params ? '?' + JSON.stringify(params) : ''); }
  catch { return url; }
}

api.getCached = async (url, opts = {}) => {
  const { ttl = 30_000, params, force = false, ...rest } = opts;
  const key = cacheKey(url, params);
  const now = Date.now();
  if (!force) {
    const hit = _cache.get(key);
    if (hit && hit.expires > now) return { data: hit.data, __cached: true };
    const inflight = _inflight.get(key);
    if (inflight) return inflight;
  }
  const p = api.get(url, { params, ...rest }).then((res) => {
    _cache.set(key, { data: res.data, expires: Date.now() + ttl });
    _inflight.delete(key);
    return res;
  }).catch((e) => {
    _inflight.delete(key);
    throw e;
  });
  _inflight.set(key, p);
  return p;
};

api.invalidateCache = (urlOrPrefix = null) => {
  if (!urlOrPrefix) { _cache.clear(); return; }
  for (const k of Array.from(_cache.keys())) {
    if (k === urlOrPrefix || k.startsWith(urlOrPrefix)) _cache.delete(k);
  }
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error.config || {};
    const url = cfg.url || '';
    // Never run the refresh/retry dance for auth endpoints themselves —
    // otherwise a failed /auth/login would fire /auth/refresh, which also
    // 401s, which dispatches `auth:logout`, which wipes the user cache
    // and shows the vague "Login fehlgeschlagen" to the user even though
    // the original error was e.g. Invalid credentials.
    const isAuthEndpoint = /\/auth\/(login|refresh|mfa\/validate|register|forgot-password|reset-password)|\/poc\/verify/.test(url);
    // Anonymous visitors have no tokens at all — every public landing-page
    // load was firing /auth/me → 401 → /auth/refresh → 401 → loop, which
    // hammered the backend on every page view. We skip the refresh dance
    // entirely when the client has no refresh token to begin with.
    const hasAnyToken = !!(getAccessToken() || getRefreshToken());
    if (error.response?.status === 401 && !cfg._retry && !isAuthEndpoint && hasAnyToken) {
      cfg._retry = true;
      try {
        const rt = getRefreshToken();
        const { data } = await axios.post(
          `${API_BASE}/api/auth/refresh`,
          rt ? { refresh_token: rt } : {},
          { withCredentials: true }
        );
        if (data?.access_token) {
          storeTokens(data.access_token, null);
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${data.access_token}`;
        }
        return api(cfg);
      } catch {
        clearTokens();
        window.dispatchEvent(new Event('auth:logout'));
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Normalize any FastAPI error payload into a readable string.
 * Handles:
 *   - plain strings (FastAPI `raise HTTPException(detail="...")`)
 *   - validation error arrays (FastAPI/Pydantic: [{loc, msg, type}, ...])
 *   - rich error objects (e.g. {message, balance, required, shortfall})
 *   - nested detail objects (returns `.message` / `.msg` / `.error` when present)
 *   - axios error objects (when called with the whole error)
 */
/**
 * Human-friendly translations for common backend error detail strings.
 * Keeps the English technical wording of the API but shows something nicer
 * in the UI. Matched as substring / exact, case-insensitive.
 */
const AUTH_ERROR_KLARTEXT = [
  [/^invalid credentials?$/i, 'Diese Kombination aus E-Mail und Passwort stimmt nicht. Bitte erneut versuchen.'],
  [/zu viele Fehlversuche/i, 'Zu viele Fehlversuche. Bitte warten Sie 15 Minuten und versuchen es dann erneut.'],
  [/email.*verified|verifizieren/i, 'Ihre E-Mail-Adresse wurde noch nicht bestätigt. Prüfen Sie Ihr Postfach nach unserer Bestätigungs-Mail.'],
  [/^account deactiviert/i, 'Ihr Konto ist deaktiviert. Bitte wenden Sie sich an unseren Support.'],
  [/Konto wartet auf Freigabe/i, 'Ihr Konto wartet noch auf Freigabe durch unser Admin-Team. Wir informieren Sie per E-Mail, sobald die Prüfung abgeschlossen ist.'],
  [/Konto wurde gesperrt/i, 'Ihr Konto wurde gesperrt. Bitte wenden Sie sich an unseren Support.'],
  [/passkey-registrierung fehlgeschlagen/i, 'Die Passkey-Einrichtung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.'],
  [/passkey konnte nicht verifiziert werden/i, 'Der Passkey wurde nicht erkannt. Bitte versuchen Sie es erneut oder wählen Sie ein anderes Anmelde-Verfahren.'],
  [/passkey.*nicht bekannt/i, 'Dieser Passkey ist nicht mit dem Konto verknüpft. Bitte wählen Sie ein anderes Anmelde-Verfahren.'],
  [/sitzung abgelaufen/i, 'Die Anmeldesitzung ist abgelaufen. Bitte starten Sie erneut.'],
  [/magic.?link/i, 'Der Anmelde-Link konnte nicht versendet werden. Bitte versuchen Sie es in einem Moment erneut.'],
  [/Link abgelaufen|magic.?link.*abgelaufen/i, 'Dieser Anmelde-Link ist abgelaufen. Bitte fordern Sie einen neuen an.'],
  [/network error|err_network/i, 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen und erneut versuchen.'],
  [/request failed with status code 5/i, 'Unser Server ist derzeit nicht erreichbar. Bitte in wenigen Augenblicken erneut versuchen.'],
];

export function formatApiError(input) {
  if (input == null) return null;
  const detail = input?.response?.data?.detail !== undefined ? input.response.data.detail : input;
  if (detail == null) return null;
  let raw;
  if (typeof detail === 'string') raw = detail;
  else if (typeof detail === 'number' || typeof detail === 'boolean') raw = String(detail);
  else if (Array.isArray(detail))
    raw = detail.map(e => e?.msg || (typeof e === 'string' ? e : JSON.stringify(e))).filter(Boolean).join(' · ');
  else if (typeof detail === 'object') {
    raw = detail.message || detail.msg || detail.error || detail.detail || JSON.stringify(detail);
  } else {
    raw = String(detail);
  }
  // Try friendly translation.
  for (const [pattern, friendly] of AUTH_ERROR_KLARTEXT) {
    if (pattern.test(raw)) return friendly;
  }
  return raw;
}

export default api;
